// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createBoostOrder, captureBoostOrder } from "../api/monetization";
import { getRuntimeEnv } from "../config/runtimeEnv";
import "../styles/PaypalBoostModal.css";

declare global {
  interface Window {
    paypal?: any;
  }
}

const PAYPAL_CLIENT_ID = getRuntimeEnv("REACT_APP_PAYPAL_CLIENT_ID");
const BOOST_SDK_SCRIPT_ID = "paypal-sdk-script-boost";
const BOOST_SDK_COMPONENTS = "buttons,card-fields";
const PAYPAL_BRAND_NAME = "Cardex";

function formatMoney(cents = 0, currency = "USD") {
  return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
}

function toCheckoutError(error) {
  const message = String(error?.message || "").trim();
  const code = String(error?.code || "").trim();
  if (code === "BOOST_PAYMENT_AMOUNT_MISMATCH") {
    return "PayPal captured an amount that does not match the saved boost quote.";
  }
  if (code === "PAYPAL_CAPTURE_NOT_COMPLETED") {
    return "PayPal approval was not completed yet. Please finish the payment and try again.";
  }
  if (code === "PAYPAL_ORDER_CREATE_TIMEOUT" || code === "PAYPAL_CAPTURE_TIMEOUT") {
    return "PayPal is taking too long to respond. Please wait a moment and retry.";
  }
  if (message) return message;
  return "PayPal checkout failed. Please try again.";
}

export default function PaypalBoostModal({ isOpen, target, quote, onClose, onSuccess }) {
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkLoading, setSdkLoading] = useState(false);
  const [sdkError, setSdkError] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutState, setCheckoutState] = useState("idle");
  const [paypalButtonEligible, setPaypalButtonEligible] = useState(false);
  const [cardFieldsEligible, setCardFieldsEligible] = useState(false);
  const [cardSubmitting, setCardSubmitting] = useState(false);
  const paypalButtonRef = useRef<HTMLDivElement | null>(null);
  const cardNameRef = useRef<HTMLDivElement | null>(null);
  const cardNumberRef = useRef<HTMLDivElement | null>(null);
  const cardExpiryRef = useRef<HTMLDivElement | null>(null);
  const cardCvvRef = useRef<HTMLDivElement | null>(null);
  const buttonInstanceRef = useRef<any>(null);
  const cardFieldsRef = useRef<any>(null);
  const lastOrderIdRef = useRef("");

  const canClose = checkoutState !== "finalizing" && !cardSubmitting;

  const statusMessage = useMemo(() => {
    if (checkoutState === "creating-order") return "Creating your PayPal order...";
    if (checkoutState === "awaiting-approval") return "Complete approval in the PayPal popup.";
    if (checkoutState === "finalizing") return "Finalizing your payment and applying the boost...";
    if (checkoutState === "success") return "Boost payment completed.";
    return "";
  }, [checkoutState]);

  const clearRenderedFields = useCallback(() => {
    if (buttonInstanceRef.current?.close) {
      buttonInstanceRef.current.close();
    }
    buttonInstanceRef.current = null;
    cardFieldsRef.current = null;
    [paypalButtonRef, cardNameRef, cardNumberRef, cardExpiryRef, cardCvvRef].forEach((ref) => {
      if (ref.current) {
        ref.current.innerHTML = "";
      }
    });
  }, []);

  const ensureSdk = useCallback(async () => {
    if (!PAYPAL_CLIENT_ID) {
      throw new Error("PayPal client id is missing.");
    }

    const hasRequiredComponents =
      typeof window.paypal?.Buttons === "function" && typeof window.paypal?.CardFields === "function";
    if (hasRequiredComponents) {
      return;
    }

    const existingScripts = Array.from(document.querySelectorAll('script[src*="paypal.com/sdk/js"]'));
    existingScripts.forEach((script) => {
      if (script.id !== BOOST_SDK_SCRIPT_ID) {
        script.parentElement?.removeChild(script);
      }
    });

    const existingBoostScript = document.getElementById(BOOST_SDK_SCRIPT_ID);
    if (existingBoostScript) {
      existingBoostScript.parentElement?.removeChild(existingBoostScript);
    }

    try {
      delete window.paypal;
    } catch {
      window.paypal = undefined;
    }

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.id = BOOST_SDK_SCRIPT_ID;
      script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
        PAYPAL_CLIENT_ID,
      )}&currency=${encodeURIComponent(String(quote?.currency || "USD"))}&intent=capture&components=${encodeURIComponent(
        BOOST_SDK_COMPONENTS,
      )}&disable-funding=${encodeURIComponent("credit,paylater")}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Could not load PayPal."));
      document.body.appendChild(script);
    });
  }, [quote?.currency]);

  const createOrder = useCallback(async () => {
    if (!target?.postType || !target?.postId) {
      throw new Error("Missing boost target.");
    }

    setCheckoutError("");
    setCheckoutState("creating-order");
    const order = await createBoostOrder({ postType: target.postType, postId: target.postId });
    const orderId = String(order?.orderId || "").trim();
    if (!orderId) {
      throw new Error("PayPal order is missing an order id.");
    }
    lastOrderIdRef.current = orderId;
    setCheckoutState("awaiting-approval");
    return orderId;
  }, [target?.postId, target?.postType]);

  const finalizeOrder = useCallback(
    async (orderId: string) => {
      const resolvedOrderId = String(orderId || lastOrderIdRef.current || "").trim();
      if (!resolvedOrderId) {
        throw new Error("Missing PayPal order id.");
      }

      setCheckoutError("");
      setCheckoutState("finalizing");
      const result = await captureBoostOrder({
        orderId: resolvedOrderId,
        postType: target.postType,
        postId: target.postId,
      });
      setCheckoutState("success");
      await Promise.resolve(onSuccess?.(result));
      onClose?.();
      return result;
    },
    [onClose, onSuccess, target?.postId, target?.postType],
  );

  const handleCardSubmit = useCallback(async () => {
    if (!cardFieldsRef.current) return;
    setCheckoutError("");
    setCardSubmitting(true);
    try {
      const state = await cardFieldsRef.current.getState();
      if (!state?.isFormValid) {
        setCheckoutError("Complete the card fields before paying.");
        return;
      }
      setCheckoutState("awaiting-approval");
      await cardFieldsRef.current.submit({
        cardholderName: target?.title || PAYPAL_BRAND_NAME,
      });
    } catch (error) {
      setCheckoutError(toCheckoutError(error));
      setCheckoutState("idle");
    } finally {
      setCardSubmitting(false);
    }
  }, [target?.title]);

  useEffect(() => {
    if (!isOpen || !quote || quote.isFree) return undefined;

    let cancelled = false;
    setSdkLoading(true);
    setSdkError("");
    setCheckoutError("");
    setCheckoutState("idle");
    setPaypalButtonEligible(false);
    setCardFieldsEligible(false);

    ensureSdk()
      .then(() => {
        if (cancelled) return;
        setSdkReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setSdkReady(false);
        setSdkError(toCheckoutError(error));
      })
      .finally(() => {
        if (!cancelled) setSdkLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ensureSdk, isOpen, quote]);

  useEffect(() => {
    if (!isOpen || !sdkReady || !window.paypal || !quote || quote.isFree) return undefined;
    if (!paypalButtonRef.current || !cardNameRef.current || !cardNumberRef.current || !cardExpiryRef.current || !cardCvvRef.current) {
      return undefined;
    }

    clearRenderedFields();
    setPaypalButtonEligible(false);
    setCardFieldsEligible(false);

    const paypalButtons = window.paypal.Buttons({
      fundingSource: window.paypal.FUNDING.PAYPAL,
      style: {
        layout: "vertical",
        shape: "rect",
        height: 48,
        label: "paypal",
      },
      createOrder,
      onApprove: async (data: any) => {
        try {
          await finalizeOrder(String(data?.orderID || "").trim());
        } catch (error) {
          setCheckoutError(toCheckoutError(error));
          setCheckoutState("idle");
        }
      },
      onCancel: () => {
        setCheckoutState("idle");
        setCheckoutError("Payment approval was cancelled.");
      },
      onError: (error: any) => {
        setCheckoutState("idle");
        setCheckoutError(toCheckoutError(error));
      },
    });

    if (paypalButtons?.isEligible?.()) {
      setPaypalButtonEligible(true);
      buttonInstanceRef.current = paypalButtons;
      paypalButtons.render(paypalButtonRef.current).catch((error: any) => {
        setPaypalButtonEligible(false);
        setCheckoutError(toCheckoutError(error));
      });
    }

    const cardFields = window.paypal.CardFields({
      createOrder,
      style: {
        input: {
          "background-color": "#ffffff",
          "border-radius": "12px",
          "font-size": "16px",
          color: "#12233f",
          "font-family": "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          "font-weight": "500",
          padding: "14px 16px",
          transition: "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
          "::placeholder": {
            color: "#70819d",
          },
        },
        ":focus": {
          "background-color": "#ffffff",
          color: "#0f1f38",
          "box-shadow": "0 0 0 1px rgba(34, 197, 255, 0.45)",
        },
        ".valid": {
          color: "#12233f",
        },
        ".invalid": {
          "background-color": "#ffffff",
          color: "#12233f",
          "box-shadow": "0 0 0 1px rgba(255, 94, 129, 0.65)",
          "::placeholder": {
            color: "#b07684",
          },
        },
      },
      onApprove: async (data: any) => {
        try {
          await finalizeOrder(String(data?.orderID || "").trim());
        } catch (error) {
          setCheckoutError(toCheckoutError(error));
          setCheckoutState("idle");
        } finally {
          setCardSubmitting(false);
        }
      },
      onError: (error: any) => {
        setCardSubmitting(false);
        setCheckoutState("idle");
        setCheckoutError(toCheckoutError(error));
      },
    });

    if (cardFields?.isEligible?.()) {
      setCardFieldsEligible(true);
      cardFieldsRef.current = cardFields;
      Promise.all([
        cardFields.NameField().render(cardNameRef.current),
        cardFields.NumberField().render(cardNumberRef.current),
        cardFields.ExpiryField().render(cardExpiryRef.current),
        cardFields.CVVField().render(cardCvvRef.current),
      ]).catch((error: any) => {
        setCardFieldsEligible(false);
        setCheckoutError(toCheckoutError(error));
      });
    }

    return () => {
      clearRenderedFields();
    };
  }, [clearRenderedFields, createOrder, finalizeOrder, isOpen, quote, sdkReady]);

  useEffect(() => {
    if (!isOpen) {
      clearRenderedFields();
      setSdkReady(false);
      setSdkLoading(false);
      setSdkError("");
      setCheckoutError("");
      setCheckoutState("idle");
      setPaypalButtonEligible(false);
      setCardFieldsEligible(false);
      setCardSubmitting(false);
      lastOrderIdRef.current = "";
    }
  }, [clearRenderedFields, isOpen]);

  if (!isOpen || !target || !quote || quote.isFree) {
    return null;
  }

  return (
    <div
      className="paypal-boost-modal-backdrop"
      onClick={() => {
        if (canClose) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="paypal-boost-modal-title"
    >
      <div className="paypal-boost-modal" onClick={(event) => event.stopPropagation()}>
        <div className="paypal-boost-modal-header">
          <div>
            <h3 id="paypal-boost-modal-title">Boost This Listing</h3>
            <p>{target.title || "Complete your boost payment securely with PayPal."}</p>
          </div>
          <button
            type="button"
            className="paypal-boost-modal-close"
            onClick={() => canClose && onClose?.()}
            disabled={!canClose}
            aria-label="Close boost checkout"
          >
            ×
          </button>
        </div>

        <div className="paypal-boost-summary">
          <div className="paypal-boost-summary-row">
            <span>Charge</span>
            <strong>{formatMoney(quote.chargeCents, quote.currency)}</strong>
          </div>
          <div className="paypal-boost-summary-row">
            <span>Plan price</span>
            <strong>{String(quote.planCode || "").toUpperCase()}</strong>
          </div>
          <div className="paypal-boost-summary-row">
            <span>Boost extension</span>
            <strong>{quote.boostExtensionDays} days</strong>
          </div>
        </div>

        {statusMessage && <div className="paypal-boost-status">{statusMessage}</div>}
        {(sdkError || checkoutError) && <div className="paypal-boost-error">{sdkError || checkoutError}</div>}

        <div className="paypal-boost-checkout">
          <section className="paypal-boost-method">
            <div className="paypal-boost-method-title">Pay with PayPal</div>
            <div ref={paypalButtonRef} className="paypal-boost-button-slot" />
            {!sdkLoading && !paypalButtonEligible && !sdkError && (
              <div className="paypal-boost-hint">PayPal button is not eligible in this browser session.</div>
            )}
          </section>

          <section className="paypal-boost-method">
            <div className="paypal-boost-method-title">Pay with card</div>
            <div className="paypal-boost-card-grid">
              <div className="paypal-boost-field">
                <label>Cardholder name</label>
                <div ref={cardNameRef} className="paypal-boost-field-slot" />
              </div>
              <div className="paypal-boost-field paypal-boost-field-full">
                <label>Card number</label>
                <div ref={cardNumberRef} className="paypal-boost-field-slot" />
              </div>
              <div className="paypal-boost-field">
                <label>Expiration date</label>
                <div ref={cardExpiryRef} className="paypal-boost-field-slot" />
              </div>
              <div className="paypal-boost-field">
                <label>Security code</label>
                <div ref={cardCvvRef} className="paypal-boost-field-slot" />
              </div>
            </div>
            <button
              type="button"
              className="paypal-boost-pay-button"
              onClick={handleCardSubmit}
              disabled={!cardFieldsEligible || cardSubmitting || checkoutState === "finalizing"}
            >
              {cardSubmitting || checkoutState === "creating-order"
                ? "Preparing payment..."
                : checkoutState === "finalizing"
                  ? "Finalizing..."
                  : `Pay ${formatMoney(quote.chargeCents, quote.currency)}`}
            </button>
            {!sdkLoading && !cardFieldsEligible && !sdkError && (
              <div className="paypal-boost-hint">Card fields are not eligible for this session.</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
